/**
 * P4-COMMERCE — the proposal ⇄ draft-order sync UI (order detail tab).
 *
 * Server side (LIVE, verified 2026-10-16): POST /api/v3/fjell/skafer/order/:id/sync
 * executes the idempotent sync plan through the lib draft-order actions; GET
 * .../synced returns the read-only snapshot. This component is the rep-facing
 * surface: edit the intended order set (Decision 005: discount + shipping
 * editable), press sync, and SEE Shopify's totals verbatim + the divergence
 * warning when the draft order no longer matches the approved proposal.
 *
 * Payment status is READ-ONLY here — this UI never writes payment state; the
 * payment view (paymentStatusView contract) renders Shopify's status verbatim
 * with the last-synced timestamp and an explicit unhealthy state on failure.
 *
 * The "create draft order" path calls the lib createDraftOrder action through
 * the same fjell API Ullr uses — never a second order path.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Grid, Paper, Stack, TextField, Typography,
} from '@mui/material';
import {
  paymentStatusView, syncIdempotencyKey,
  type IntendedLineItem, type IntendedOrderSet, type SyncedDraftOrder,
} from '../contracts/proposalSync';
import { renderOrderProposalSheet, draftRideParagraph } from '../contracts/orderProposalSheet';

export type ApiFn = (path: string, token: string, init?: RequestInit) => Promise<unknown>;

/** One editable line-item row in the intended set editor. */
const ITEM_TYPES: IntendedLineItem['itemType'][] = ['Ski Package', 'Binding', 'Top Sheet', 'Other'];

/**
 * salesQuestion id 12 — the 'How they'll ride (proposal copy…)' reference row
 * (created 2026-10-30 via /fjell/reference/salesQuestion/). The designer's
 * edited ride text persists as THIS ORDER's answer to that question, so every
 * device, rep, and future session sees the same copy (Pete 2026-10-28
 * directive: designer edits must be the order's, not the device's).
 */
const RIDE_QUESTION_ID = 12;

/**
 * salesQuestion id 13 (Pete 2026-11-02): the proposal EMAIL's opening
 * paragraph ("Hi <first>,\nYour custom ski design is ready..."). Designer-
 * editable, persisted as THIS ORDER's answer so every device/rep sees the
 * same copy - the email builder reads it verbatim. The default composes the
 * greeting from the order's customer first name + the approved template copy.
 */
const INTRO_QUESTION_ID = 13;
const DEFAULT_INTRO_BODY = "Your custom ski design is ready. Everything below is built around your measurements and preferences - here's the ski we'll press for you. A print-ready spec sheet is attached.";

function emptyItem(): IntendedLineItem {
  return { variantId: null, title: '', quantity: 1, price: '0.00', itemType: 'Other' };
}

export function ProposalPayments({
  orderId,
  designVersion,
  api,
  token,
  canEdit = true,
}: {
  orderId: number;
  /** The approved proposal version this set is quoted from (Decision 001). */
  designVersion: number;
  api: ApiFn;
  token: string;
  canEdit?: boolean;
}) {
  const [items, setItems] = useState<IntendedLineItem[]>([emptyItem()]);
  const [discountDesc, setDiscountDesc] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed_amount'>('percentage');
  const [shippingPrice, setShippingPrice] = useState('');
  const [shippingTitle, setShippingTitle] = useState('');
  const [synced, setSynced] = useState<SyncedDraftOrder | null>(null);
  /** HOW THEY'LL RIDE draft (Pete 2026-10-28): auto-drafted, designer-edited.
   * null = seed still loading; string = the text that goes on the sheet. */
  const [rideText, setRideText] = useState<string | null>(null);
  /** The order's saved answer row for RIDE_QUESTION_ID (null = none yet). */
  const [rideAnswerId, setRideAnswerId] = useState<number | null>(null);
  const rideDraftKey = `skafer:ride:${orderId}`;
  // The proposal email's opening paragraph (Pete 2026-11-02): designer-
  // editable, persisted as THIS ORDER's answer to salesQuestion 13 - same
  // priority chain as ride copy (order saved > device draft > default).
  const [introText, setIntroText] = useState<string | null>(null);
  const [introAnswerId, setIntroAnswerId] = useState<number | null>(null);
  const introDraftKey = `skafer:intro:${orderId}`;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const intended: IntendedOrderSet = useMemo(() => ({
    orderId,
    designVersion,
    items,
    discount: discountDesc.trim() && discountValue.trim()
      ? { description: discountDesc.trim(), valueType: discountType, value: discountValue.trim() }
      : null,
    shipping: shippingPrice.trim()
      ? { custom: true, price: shippingPrice.trim(), title: shippingTitle.trim() || 'Shipping' }
      : null,
  }), [orderId, designVersion, items, discountDesc, discountValue, discountType, shippingPrice, shippingTitle]);

  const view = paymentStatusView(intended, synced, null);

  /** Resolve the shopifyCustomerPk for this order's buyer (the draft order's owner). */
  const resolveShopifyCustomer = async (): Promise<string | null> => {
    const order = await api(`/api/v3/fjell/order/${orderId}`, token) as Record<string, unknown>;
    const buyerId = Number((order as { buyerId?: number }).buyerId
      ?? (order as { item?: { buyerId?: number } }).item?.buyerId);
    if (!buyerId) return null;
    const customer = await api(`/api/v3/fjell/customer/${buyerId}`, token) as Record<string, unknown>;
    const row = (customer as { item?: Record<string, unknown> }).item ?? customer;
    const pk = row.shopifyCustomerId;
    return pk ? String(pk) : null;
  };

  const createDraftOrder = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      const ck = await resolveShopifyCustomer();
      if (!ck) {
        setError('This customer has no Shopify customer record — create one in Ullr first (Skafer never invents Shopify accounts).');
        return;
      }
      // lib createDraftOrder action (same path Ullr's AssociateOrCreateNew uses).
      await api(`/api/v3/fjell/shopify/customer/${ck}/draftOrder/createDraftOrder`, token, {
        method: 'POST',
        body: JSON.stringify({ shopifyCustomerPk: ck, orderId }),
      });
      setInfo('Draft order created and linked to this order. Now press Sync to apply the proposal.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setError(null); setBusy(true);
    try {
      const ck = await resolveShopifyCustomer();
      if (!ck) { setError('Customer has no Shopify record — nothing to read.'); return; }
      const res = await api(`/api/v3/fjell/skafer/order/${orderId}/synced?shopifyCustomerPk=${ck}`, token);
      setSynced(res === null || res === undefined ? null : res as SyncedDraftOrder);
      setInfo(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Load the synced snapshot on mount — a reload must not blank the money view
  // (the draft exists server-side; only the click-triggered refresh knew it).
  // When the snapshot arrives and the editor is still untouched (blank row is
  // the pristine state), seed the editor from Shopify so divergence means
  // REAL edits, not reload-amnesia.
  const editorSeeded = useRef(false);
  useEffect(() => {
    if (canEdit) {
      void refresh();
    }
  }, [orderId]);

  useEffect(() => {
    if (!canEdit || !synced || editorSeeded.current) return;
    editorSeeded.current = true;
    const untouched = items.length === 1 && !items[0].title && items[0].price === '0.00'
      && !discountDesc.trim() && !discountValue.trim()
      && !shippingPrice.trim() && !shippingTitle.trim();
    if (!untouched) return;
    setItems(synced.items.length
      ? synced.items.map((li) => ({ ...li }))
      : [emptyItem()]);
    if (synced.discount) {
      setDiscountDesc(synced.discount.description);
      setDiscountType(synced.discount.valueType);
      setDiscountValue(synced.discount.value);
    }
    if (synced.shipping) {
      setShippingPrice(synced.shipping.price ?? '');
      setShippingTitle(synced.shipping.title ?? '');
    }
  }, [canEdit, synced, items, discountDesc, discountValue, shippingPrice, shippingTitle]);

  /** Send the Shopify draft invoice (lib sendInvoice action — same path Ullr uses). */
  const sendInvoice = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      const ck = await resolveShopifyCustomer();
      if (!ck) { setError('Customer has no Shopify record — nothing to invoice.'); return; }
      const order = await api(`/api/v3/fjell/order/${orderId}`, token) as Record<string, unknown>;
      const detail = (order as { item?: Record<string, unknown> }).item ?? order;
      const draftId = detail.shopifyDraftOrderId ? String(detail.shopifyDraftOrderId) : synced?.shopifyDraftOrderId;
      if (!draftId) { setError('No draft order linked yet — create and sync it first.'); return; }
      await api(`/api/v3/fjell/shopify/customer/${ck}/draftOrder/${draftId}/sendInvoice`, token, {
        method: 'POST',
        body: JSON.stringify({ subject: `Your Wagner Custom Skis proposal`, customMessage: '' }),
      });
      await refresh();
      setInfo('Draft invoice sent — the customer gets the Shopify email with a pay link.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** The draft invoice link (pay/check-out page) — Shopify generates it for every draft. */
  const invoiceLink = synced?.invoiceUrl ?? '';

  /**
   * HOW THEY'LL RIDE first draft (Pete 2026-10-28): auto-generate from this
   * order's own form + outline (draftRideParagraph — deterministic), unless
   * the designer already saved an edit for this order (localStorage, per
   * device). The designer freely modifies; Generate uses the box verbatim.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const finish = (text: string) => { if (!cancelled) setRideText(text); };
      // Priority 1: the ORDER's saved copy (any device, any rep) — the row's
      // presence is the designer's saved intent, even when saved empty.
      try {
        const answers = (await api(`/api/v3/fjell/order/${orderId}/salesQuestionAnswer/`, token)) as { items?: Array<Record<string, unknown>> };
        const mine = (answers?.items ?? []).find((a) => Number(a.salesQuestionId) === RIDE_QUESTION_ID) as { id?: number; answer?: string } | undefined;
        if (mine) {
          if (!cancelled) setRideAnswerId(Number(mine.id) || null);
          if (typeof mine.answer === 'string') { finish(mine.answer); return; }
        }
      } catch { /* fall through to the next priority */ }
      // Priority 2: this device's last copy (offline fallback).
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(rideDraftKey) : null;
      if (saved !== null) { finish(saved); return; }
      // Priority 3: the deterministic auto-draft.
      try {
        const forms = (await api(`/api/v3/fjell/order/${orderId}/orderForm/`, token)) as { items?: Array<Record<string, unknown>> };
        const form = forms?.items?.[0] as Record<string, unknown> | undefined;
        if (!form?.id) { finish(''); return; }
        const dxf = (await api(`/api/v3/fjell/order/${orderId}/orderForm/${form.id}/dxfSki`, token)) as { arcs1?: Array<Record<string, unknown>> };
        if (!dxf?.arcs1) { finish(''); return; }
        finish(draftRideParagraph(form, dxf.arcs1));
      } catch {
        finish('');
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, api, token]);

  /** Save the designer's ride copy to THE ORDER (any device/rep sees it):
   *  POST the first save, PUT updates afterwards. */
  const saveRideText = async () => {
    if (rideText === null) return;
    setError(null); setInfo(null); setBusy(true);
    try {
      const updating = rideAnswerId !== null;
      const result = await api(
        updating
          ? `/api/v3/fjell/order/${orderId}/salesQuestionAnswer/${rideAnswerId}`
          : `/api/v3/fjell/order/${orderId}/salesQuestionAnswer`,
        token,
        {
          method: updating ? 'PUT' : 'POST',
          body: JSON.stringify(updating
            ? { answer: rideText }
            : { orderId, salesQuestionId: RIDE_QUESTION_ID, answer: rideText }),
        }) as { id?: number };
      if (!updating && result?.id) setRideAnswerId(Number(result.id));
      setInfo('Ride copy saved to this order — every rep and device now sees it.');
    } catch (e) {
      setError(`Could not save the ride copy: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onRideTextChange = (value: string) => {
    setRideText(value);
    try { if (typeof localStorage !== 'undefined') localStorage.setItem(rideDraftKey, value); } catch { /* private mode etc. */ }
  };

  const resetRideDraft = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      const forms = (await api(`/api/v3/fjell/order/${orderId}/orderForm/`, token)) as { items?: Array<Record<string, unknown>> };
      const form = forms?.items?.[0] as Record<string, unknown> | undefined;
      const dxf = form?.id
        ? (await api(`/api/v3/fjell/order/${orderId}/orderForm/${form.id}/dxfSki`, token)) as { arcs1?: Array<Record<string, unknown>> }
        : null;
      const draft = form && dxf?.arcs1 ? draftRideParagraph(form, dxf.arcs1) : '';
      onRideTextChange(draft);
      setInfo('Reset to the auto-generated first draft.');
    } catch (e) {
      setError(`Could not re-draft: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * INTRO PARAGRAPH (Pete 2026-11-02): the order's saved copy (any device/
   * rep) > this device's last edit > the default paragraph. The default
   * greets the order's customer by first name + the approved template copy.
   */
  const composeIntroDefault = async (): Promise<string> => {
    try {
      const forms = (await api(`/api/v3/fjell/order/${orderId}/orderForm/`, token)) as { items?: Array<Record<string, unknown>> };
      const form = forms?.items?.[0] as Record<string, unknown> | undefined;
      const full = String(form?.handcraftedFor ?? '').trim();
      const first = full ? full.split(/\s+/)[0] : 'there';
      return `Hi ${first},\n${DEFAULT_INTRO_BODY}`;
    } catch {
      return `Hi there,\n${DEFAULT_INTRO_BODY}`;
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const finish = (text: string) => { if (!cancelled) setIntroText(text); };
      // Priority 1: the ORDER's saved copy (the row's presence is the
      // designer's saved intent, even when saved empty).
      try {
        const answers = (await api(`/api/v3/fjell/order/${orderId}/salesQuestionAnswer/`, token)) as { items?: Array<Record<string, unknown>> };
        const mine = (answers?.items ?? []).find((a) => Number(a.salesQuestionId) === INTRO_QUESTION_ID) as { id?: number; answer?: string } | undefined;
        if (mine) {
          if (!cancelled) setIntroAnswerId(Number(mine.id) || null);
          if (typeof mine.answer === 'string') { finish(mine.answer); return; }
        }
      } catch { /* fall through to the next priority */ }
      // Priority 2: this device's last edit (offline fallback).
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(introDraftKey) : null;
      if (saved !== null) { finish(saved); return; }
      // Priority 3: the default paragraph.
      finish(await composeIntroDefault());
    })();
    return () => { cancelled = true; };
  }, [orderId, api, token]);

  /** Save the designer's intro copy to THE ORDER (any device/rep sees it):
   *  POST the first save, PUT updates afterwards. */
  const saveIntroText = async () => {
    if (introText === null) return;
    setError(null); setInfo(null); setBusy(true);
    try {
      const updating = introAnswerId !== null;
      const result = await api(
        updating
          ? `/api/v3/fjell/order/${orderId}/salesQuestionAnswer/${introAnswerId}`
          : `/api/v3/fjell/order/${orderId}/salesQuestionAnswer`,
        token,
        {
          method: updating ? 'PUT' : 'POST',
          body: JSON.stringify(updating
            ? { answer: introText }
            : { orderId, salesQuestionId: INTRO_QUESTION_ID, answer: introText }),
        }) as { id?: number };
      if (!updating && result?.id) setIntroAnswerId(Number(result.id));
      setInfo('Intro copy saved to this order - the proposal email opens with exactly this text.');
    } catch (e) {
      setError(`Could not save the intro copy: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onIntroTextChange = (value: string) => {
    setIntroText(value);
    try { if (typeof localStorage !== 'undefined') localStorage.setItem(introDraftKey, value); } catch { /* private mode etc. */ }
  };

  const resetIntroDraft = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      onIntroTextChange(await composeIntroDefault());
      setInfo('Reset to the default intro paragraph (greeting + template copy).');
    } catch (e) {
      setError(`Could not reset the intro copy: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * ONE-CLICK PROPOSAL (Pete 2026-10-28): fetch the live order facets
   * (orderForm + dxfSki + calcSki1 — same routes the CLI uses) and render
   * the customer-facing proposal sheet PDF in the browser via the SHARED
   * adapter (src/contracts/orderProposalSheet.ts; deterministic, all numbers
   * sourced, 230702 control-tested). The PRICING section embeds the draft
   * invoice link when one is synced.
   */
  const generateProposalSheet = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      const forms = (await api(`/api/v3/fjell/order/${orderId}/orderForm/`, token)) as { items?: Array<Record<string, unknown>> };
      const form = forms?.items?.[0] as Record<string, any> | undefined;
      if (!form?.id) { setError('This order has no order form yet — nothing to render.'); return; }
      const dxf = (await api(`/api/v3/fjell/order/${orderId}/orderForm/${form.id}/dxfSki`, token)) as { arcs1?: unknown };
      const calc = (await api(`/api/v3/fjell/order/${orderId}/orderForm/${form.id}/calcSki1`, token)) as { edgeFrame?: Record<string, number> };
      if (!dxf?.arcs1 || !calc?.edgeFrame) { setError('Design geometry is not on record for this order yet.'); return; }
      const { bytes, fileName } = renderOrderProposalSheet({
        form, dxfArcs: dxf.arcs1 as Array<Record<string, unknown>>, edgeFrame: calc.edgeFrame,
        invoiceUrl: invoiceLink || undefined, designVersion,
        // The designer's edited ride text (null while the seed loads ->
        // auto-draft; '' hides the section).
        rideText: rideText ?? undefined,
      });
      const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setInfo(`Proposal sheet downloaded (${fileName})${invoiceLink
        ? ' — PRICING links to the draft invoice.'
        : ' — create + sync the draft order to include the invoice link.'}`);
    } catch (e) {
      setError(`Proposal sheet failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setError(null); setInfo(null); setBusy(true);
    try {
      const ck = await resolveShopifyCustomer();
      if (!ck) {
        setError('This customer has no Shopify customer record — sync has nothing to write to.');
        return;
      }
      const res = await api(`/api/v3/fjell/skafer/order/${orderId}/sync`, token, {
        method: 'POST',
        body: JSON.stringify({ ...intended, shopifyCustomerPk: ck }),
      }) as { status?: string; error?: string; synced?: SyncedDraftOrder };
      if (res && res.status === 'failed') {
        setError(`Sync failed (safe to retry): ${res.error ?? 'unknown error'}`);
        return;
      }
      setSynced(res.synced ?? null);
      setInfo(res.status === 'applied-noop'
        ? 'Already in sync — no changes were needed.'
        : 'Sync applied. Totals below are Shopify\'s (single source of truth).');
    } catch (e) {
      setError(`Sync failed (safe to retry): ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const updateItem = (idx: number, patch: Partial<IntendedLineItem>) => {
    setItems((rows) => rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" onClose={() => setInfo(null)}>{info}</Alert>}

      {/* Payment + sync status (read-only) */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" color="primary">Draft order status</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={view.syncHealthy ? `Payment: ${view.paymentStatus}` : 'Sync state unknown'}
              color={view.paymentStatus === 'PAID' ? 'success' : view.syncHealthy ? 'default' : 'warning'}
            />
            {view.lastSyncedAt && (
              <Typography variant="caption" color="text.secondary">
                synced {new Date(view.lastSyncedAt).toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Typography variant="h5" sx={{ mt: 1 }}>
          {view.totalPrice === '—' ? 'No draft order yet' : `Shopify total: $${view.totalPrice}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Shopify's total is the single source of truth — Skafer never computes prices.
        </Typography>
        {view.divergences.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <b>Out of sync:</b> {view.divergences.map((d) => d.detail).join(' · ')}
          </Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" onClick={refresh} disabled={busy || !canEdit}>
            Refresh from Shopify
          </Button>
          {synced === null && canEdit && (
            <Button size="small" variant="outlined" onClick={createDraftOrder} disabled={busy}>
              Create draft order
            </Button>
          )}
          {synced && canEdit && (
            <Button size="small" variant="outlined" onClick={sendInvoice} disabled={busy}>
              Send draft invoice
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={generateProposalSheet} disabled={busy}>
            Generate proposal sheet
          </Button>
          {invoiceLink && (
            <Button size="small" variant="text" href={invoiceLink} target="_blank" rel="noopener noreferrer" disabled={busy}>
              Open invoice link ↗
            </Button>
          )}
        </Stack>
      </Paper>

      {/* PROPOSAL EMAIL INTRO — designer-editable opening paragraph (Pete 2026-11-02) */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" color="primary">Proposal email intro</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={saveIntroText} disabled={busy || introText === null} aria-label="Save intro copy">
              Save to order
            </Button>
            <Button size="small" variant="outlined" onClick={resetIntroDraft} disabled={busy || introText === null} aria-label="Reset intro default">
              Reset to default
            </Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          The paragraph that opens the HTML proposal email (greeting + intro). Edit freely and
          Save to order so every rep and device sees your copy; the email builder sends it verbatim.
        </Typography>
        <TextField
          fullWidth multiline minRows={3}
          value={introText ?? ''}
          onChange={(e) => onIntroTextChange(e.target.value)}
          placeholder={introText === null ? 'Loading intro…' : ''}
          disabled={introText === null}
          inputProps={{ 'aria-label': "Proposal email intro" }}
        />
      </Paper>

      {/* HOW THEY'LL RIDE — auto-drafted, designer-editable (Pete 2026-10-28) */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" color="primary">How they'll ride</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={saveRideText} disabled={busy || rideText === null} aria-label="Save ride copy">
              Save to order
            </Button>
            <Button size="small" variant="outlined" onClick={resetRideDraft} disabled={busy || rideText === null} aria-label="Reset ride draft">
              Reset to auto-draft
            </Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Auto-generated first draft from this order's design — edit freely. Save to order so
          every rep and device sees your copy; Generate puts this exact text on the sheet
          (empty = section hidden).
        </Typography>
        <TextField
          fullWidth multiline minRows={5}
          value={rideText ?? ''}
          onChange={(e) => onRideTextChange(e.target.value)}
          placeholder={rideText === null ? 'Loading first draft…' : ''}
          disabled={rideText === null}
          inputProps={{ 'aria-label': "How they'll ride" }}
        />
      </Paper>

      {/* Intended set editor */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" color="primary" gutterBottom>
          What the customer buys (proposal v{designVersion})
        </Typography>
        {items.map((item, idx) => (
          <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                select label="Type" size="small" fullWidth value={item.itemType}
                disabled={!canEdit}
                onChange={(e) => updateItem(idx, { itemType: e.target.value as IntendedLineItem['itemType'] })}
                SelectProps={{ native: true }}
              >
                {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label='Title (required)'
                size="small" fullWidth value={item.title} disabled={!canEdit}
                onChange={(e) => updateItem(idx, { title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                label="Qty" size="small" fullWidth type="number" value={item.quantity}
                disabled={!canEdit}
                onChange={(e) => updateItem(idx, { quantity: Math.max(1, Number(e.target.value) || 1) })}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                label="Unit price" size="small" fullWidth value={item.price} disabled={!canEdit}
                onChange={(e) => updateItem(idx, { price: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 1 }}>
              <Button
                size="small" disabled={!canEdit || items.length === 1}
                onClick={() => setItems((rows) => rows.filter((_, i) => i !== idx))}
              >
                ✕
              </Button>
            </Grid>
          </Grid>
        ))}
        <Button size="small" disabled={!canEdit} onClick={() => setItems((rows) => [...rows, emptyItem()])}>
          + Add line
        </Button>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Discount (single slot, per Decision 005)</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Description" size="small" fullWidth value={discountDesc} disabled={!canEdit}
            onChange={(e) => setDiscountDesc(e.target.value)}
          />
          <TextField
            select label="Type" size="small" sx={{ width: 150 }} value={discountType}
            disabled={!canEdit} onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed_amount')}
            SelectProps={{ native: true }}
          >
            <option value="percentage">%</option>
            <option value="fixed_amount">$</option>
          </TextField>
          <TextField
            label="Value" size="small" sx={{ width: 120 }} value={discountValue} disabled={!canEdit}
            onChange={(e) => setDiscountValue(e.target.value)}
          />
        </Stack>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Shipping</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Price ($)" size="small" sx={{ width: 140 }} value={shippingPrice} disabled={!canEdit}
            onChange={(e) => setShippingPrice(e.target.value)}
          />
          <TextField
            label="Title" size="small" fullWidth value={shippingTitle} disabled={!canEdit}
            onChange={(e) => setShippingTitle(e.target.value)}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" onClick={sync} disabled={busy || !canEdit}>
            {busy ? 'Working…' : 'Sync proposal to Shopify'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            key {syncIdempotencyKey(intended).slice(-12)} — double-clicks converge, failures release the key
          </Typography>
        </Stack>
      </Paper>

      {/* Synced snapshot detail */}
      {synced && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            What Shopify has (synced snapshot)
          </Typography>
          {synced.items.map((li, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                {li.title} × {li.quantity} <Chip size="small" label={li.itemType} sx={{ ml: 1, height: 18 }} />
              </Typography>
              <Typography variant="body2">${(Number(li.price) * li.quantity).toFixed(2)}</Typography>
            </Box>
          ))}
          {synced.discount && (
            <Typography variant="body2" color="text.secondary">
              Discount: {synced.discount.description} ({synced.discount.valueType === 'percentage' ? `${synced.discount.value}%` : `$${synced.discount.value}`})
            </Typography>
          )}
          {synced.shipping && (
            <Typography variant="body2" color="text.secondary">
              Shipping: {synced.shipping.title} — ${synced.shipping.price}
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Total: ${synced.totalPrice} · payment {synced.financialStatus}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
